import React, { useState, useMemo, useCallback } from 'react';
import { Calendar, ChevronDown, ChevronUp, RefreshCw, Expand, Minimize, Copy, Hotel } from 'lucide-react';
import { Button } from './ui';
import api from '../services/api';

interface Activity {
  day: number;
  tour_title: string;
  lead_guest: string;
  agency_name?: string;
  itinerary_id: number;
  itinerary_version: string;
  checkout: boolean;
  departure?: boolean;
  day_detail: any;
  accommodation_info?: {
    hotel_names: string[];
    checkout_date?: string;
    checkin_date?: string;
    is_departure?: boolean;
  };
}


const CalendarView: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedActivities, setExpandedActivities] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  const loadActivities = useCallback(async () => {
    setLoading(true);
    setError(null);
    setHasSearched(true);
    try {
      const response = await api.get('/calendar/activities', {
        params: { date: selectedDate }
      });
      setActivities(response.data || []);
    } catch (error) {
      console.error('Failed to load activities:', error);
      setError('Failed to load activities. Please try again.');
      setActivities([]);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  const handleDateChange = (newDate: string) => {
    setSelectedDate(newDate);
    setHasSearched(false);
    setActivities([]);
    setError(null);
  };

  const copyActivityText = useCallback((activity: Activity) => {
    const date = new Date(selectedDate);
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const formattedDate = `${monthNames[date.getMonth()]} ${date.getDate()}`;
    
    // Extract activities from tour description
    const tourDesc = activity.day_detail?.tour_description || '';
    const tourActivities = tourDesc
      .split('\n')
      .filter((line: string) => line.trim().startsWith('•') || line.trim().startsWith('-'))
      .map((line: string) => line.replace(/^[•-]\s*/, '').trim())
      .filter((line: string) => line && !line.toLowerCase().includes('breakfast') && !line.toLowerCase().includes('accommodation'))
      .map((line: string) => line.split(/[:\-–]/)[0].trim());

    // Build addon map keyed by main_activity_name
    const addonsByActivity: Record<string, string[]> = {};
    const generalAddons: string[] = [];

    if (activity.day_detail?.day_addons) {
      activity.day_detail.day_addons.forEach((addon: any) => {
        if (!addon.activity_name) return;
        const qty = addon.quantity && addon.quantity > 0 ? addon.quantity : 1;
        const text = `✨ ${qty}x ${addon.activity_name}`;
        const key = addon.main_activity_name;
        if (key && key !== 'General') {
          (addonsByActivity[key] = addonsByActivity[key] || []).push(text);
        } else {
          generalAddons.push(text);
        }
      });
    }

    if (activity.day_detail?.day_extras) {
      activity.day_detail.day_extras
        .filter((extra: any) => extra.display_in_itinerary !== false && extra.number > 0 && extra.extra_text)
        .forEach((extra: any) => {
          const text = `✨ ${extra.number}x ${extra.extra_text}`;
          const key = extra.main_activity_name;
          if (key && key !== 'General') {
            (addonsByActivity[key] = addonsByActivity[key] || []).push(text);
          } else {
            generalAddons.push(text);
          }
        });
    }

    // Build schedule based on checkout status
    let schedule = [];
    
    if (activity.checkout) {
      schedule.push(`🚨 Checkout from the property`);
      schedule.push(`🚐 09:00 Pickup for tour`);
    } else {
      schedule.push(`🚐 09:00 Pickup`);
    }
    
    const hasLinkedAddons = Object.keys(addonsByActivity).length > 0;

    if (hasLinkedAddons) {
      // Grouped format: activities with linked addons nested underneath
      tourActivities.forEach((name: string) => {
        schedule.push('');
        schedule.push(`📍 ${name}`);
        Object.keys(addonsByActivity).forEach((key) => {
          if (name.toLowerCase().includes(key.toLowerCase()) || key.toLowerCase().includes(name.toLowerCase())) {
            addonsByActivity[key].forEach(a => schedule.push(`   ${a}`));
            delete addonsByActivity[key];
          }
        });
      });
      // Remaining linked addons that didn't fuzzy-match
      Object.values(addonsByActivity).forEach((items) => {
        items.forEach(a => schedule.push(`   ${a}`));
      });
      // General addons after all activities
      if (generalAddons.length > 0) {
        generalAddons.forEach(a => schedule.push(a));
      }
    } else {
      // Flat format: all activities then all general addons
      tourActivities.forEach((name: string) => schedule.push(`📍 ${name}`));
      if (generalAddons.length > 0) {
        generalAddons.forEach(a => schedule.push(a));
      }
    }
    
    // Add return/check-in info
    schedule.push('');
    if (activity.departure) {
      schedule.push(`✈️ Airport transfer & departure`);
    } else if (activity.checkout) {
      const newHotel = activity.accommodation_info?.hotel_names?.[0] || 'next hotel';
      schedule.push(`🏨 Check-in ${newHotel}`);
    } else {
      const currentHotel = activity.accommodation_info?.hotel_names?.[0] || 'hotel';
      schedule.push(`🏨 Return to ${currentHotel}`);
    }

    // Determine essentials
    let essentials = 'Essentials: Sunscreen, comfortable shoes';
    if (activity.checkout) {
      essentials = 'Essentials: Pack all belongings, sunscreen';
    }
    if (activity.departure) {
      essentials = 'Essentials: Pack everything, travel documents';
    }

    const note = `📝 _Order of activities may change based on traffic and ground conditions. Please discuss with the driver before the trip starts._`;

    const whatsappMessage = `*Day ${activity.day} • ${formattedDate} • ${activity.lead_guest}*\n\n${schedule.join('\n')}\n\n${note}\n\n${essentials}\nQuestions? Reply here! 😊`;

    navigator.clipboard.writeText(whatsappMessage).then(() => {
      console.log('WhatsApp message copied to clipboard');
    }).catch(err => {
      console.error('Failed to copy text: ', err);
    });
  }, [selectedDate]);

  const toggleActivity = useCallback((index: number) => {
    setExpandedActivities(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(index)) {
        newExpanded.delete(index);
      } else {
        newExpanded.add(index);
      }
      return newExpanded;
    });
  }, []);

  const formattedDate = useMemo(() => {
    return new Date(selectedDate).toLocaleDateString('en-GB', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }, [selectedDate]);

  const activityStats = useMemo(() => {
    const checkouts = activities.filter(a => a.checkout).length;
    const departures = activities.filter(a => a.departure).length;
    return { total: activities.length, checkouts, departures };
  }, [activities]);

  return (
    <div className="h-screen bg-gray-50 flex flex-col">
      <div className="w-full px-4 sm:px-6 lg:px-8 py-6 sm:py-8 flex-1 overflow-y-auto">
        {/* Header */}
        <div className="mb-4 sm:mb-6 animate-fade-in">
          <h1 className="text-2xl font-bold text-gray-900 mb-1 sm:mb-2">
            Calendar View
          </h1>
          <p className="text-sm sm:text-base text-gray-600">View daily activities and itineraries</p>
        </div>

        {/* Date Selector */}
        <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4 mb-4 sm:mb-6">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-blue-600" />
                <label className="text-sm font-medium text-gray-700">Date:</label>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => handleDateChange(e.target.value)}
                  disabled={loading}
                  className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-base"
                />
                <Button
                  variant="primary"
                  icon={RefreshCw}
                  onClick={loadActivities}
                  disabled={loading}
                  className="w-full sm:w-auto px-3 py-1.5 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded-lg"
                >
                  Load Activities
                </Button>
              </div>
            </div>
            {activityStats.total > 0 && (
              <div className="flex flex-wrap gap-2 text-xs sm:text-sm text-gray-600 pt-2 border-t border-gray-100">
                <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded">{activityStats.total} activities</span>
                {activityStats.checkouts > 0 && (
                  <span className="flex items-center gap-1 bg-orange-50 text-orange-700 px-2 py-1 rounded">
                    <Hotel className="h-3 w-3" />
                    {activityStats.checkouts} checkouts
                  </span>
                )}
                {activityStats.departures > 0 && (
                  <span className="bg-red-50 text-red-700 px-2 py-1 rounded">{activityStats.departures} departures</span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Activities */}
        <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-6">
          {hasSearched && (
            <div className="mb-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-1">
                    Activities for {formattedDate}
                  </h2>
                  <p className="text-xs sm:text-sm text-gray-600">
                    {activityStats.total} {activityStats.total === 1 ? 'activity' : 'activities'} found
                  </p>
                </div>
                {activities.length > 0 && (
                  <div className="flex gap-2 w-full sm:w-auto">
                    <Button
                      variant="secondary"
                      icon={Expand}
                      onClick={() => setExpandedActivities(new Set(activities.map((_, idx) => idx)))}
                      className="flex-1 sm:flex-none text-xs px-2 py-1.5 bg-blue-400 hover:bg-blue-500 text-white rounded-lg"
                    >
                      <span className="hidden sm:inline">Expand All</span>
                      <span className="sm:hidden">Expand</span>
                    </Button>
                    <Button
                      variant="secondary"
                      icon={Minimize}
                      onClick={() => setExpandedActivities(new Set())}
                      className="flex-1 sm:flex-none text-xs px-2 py-1.5 bg-blue-400 hover:bg-blue-500 text-white rounded-lg"
                    >
                      <span className="hidden sm:inline">Collapse All</span>
                      <span className="sm:hidden">Collapse</span>
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <RefreshCw className="animate-spin h-8 w-8 text-blue-600" />
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <div className="text-red-500 mb-4">{error}</div>
              <Button variant="primary" onClick={loadActivities}>
                Try Again
              </Button>
            </div>
          ) : !hasSearched ? (
            <div className="text-center py-8 sm:py-16">
              <Calendar className="h-12 w-12 sm:h-16 sm:w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg sm:text-xl font-semibold text-gray-500 mb-2">Select a Date</h3>
              <p className="text-sm sm:text-base text-gray-400">Choose a date and click refresh to view activities</p>
            </div>
          ) : activities.length === 0 ? (
            <div className="text-center py-8 sm:py-16">
              <Calendar className="h-12 w-12 sm:h-16 sm:w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg sm:text-xl font-semibold text-gray-500 mb-2">No Activities Found</h3>
              <p className="text-sm sm:text-base text-gray-400">No activities scheduled for this date</p>
            </div>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="block sm:hidden space-y-3">
                {activities.map((activity, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                    {/* Card Header */}
                    <div className="p-4 border-b border-gray-100">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-mono text-blue-600 bg-blue-50 px-2 py-1 rounded">WH_{activity.itinerary_id}</span>
                          <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">Day {activity.day}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {activity.checkout && (
                            <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">
                              Checkout
                            </span>
                          )}
                          {activity.departure && (
                            <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">
                              Departure
                            </span>
                          )}
                        </div>
                      </div>
                      <h3 className="font-semibold text-gray-900 text-sm mb-1">{activity.lead_guest}</h3>
                      <p className="text-xs text-gray-600 mb-2">{activity.tour_title}</p>
                      {activity.agency_name && (
                        <p className="text-xs text-gray-500">• {activity.agency_name}</p>
                      )}
                      {activity.accommodation_info?.hotel_names && activity.accommodation_info.hotel_names.length > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          🏨 {activity.accommodation_info.hotel_names.join(', ')}
                        </p>
                      )}
                    </div>
                    
                    {/* Card Actions */}
                    <div className="p-3 bg-gray-50 flex items-center justify-between">
                      <button
                        onClick={() => toggleActivity(index)}
                        className="flex items-center gap-2 text-sm text-blue-600 font-medium"
                      >
                        {expandedActivities.has(index) ? (
                          <><ChevronUp className="h-4 w-4" /> Hide Details</>
                        ) : (
                          <><ChevronDown className="h-4 w-4" /> View Details</>
                        )}
                      </button>
                      <button
                        onClick={() => copyActivityText(activity)}
                        className="flex items-center gap-2 text-xs text-green-600 font-medium bg-green-50 px-2 py-1 rounded-lg"
                      >
                        <Copy className="h-3 w-3" /> Copy WhatsApp
                      </button>
                    </div>
                    
                    {/* Expanded Content */}
                    {expandedActivities.has(index) && (
                      <div className="p-4 border-t border-gray-100 bg-white">
                        {/* Accommodation Details */}
                        {activity.accommodation_info && (activity.accommodation_info.hotel_names?.length > 0 || activity.accommodation_info.checkin_date || activity.accommodation_info.checkout_date) && (
                          <div className={`p-3 rounded-lg mb-3 ${
                            activity.departure ? 'bg-red-50' : 
                            activity.checkout ? 'bg-orange-50' : 'bg-blue-50'
                          }`}>
                            <h4 className={`text-xs font-semibold mb-2 ${
                              activity.departure ? 'text-red-800' : 
                              activity.checkout ? 'text-orange-800' : 'text-blue-800'
                            }`}>
                              {activity.departure ? '✈️ Departure Day' : 
                               activity.checkout ? '🏨 Checkout Day' : '🏨 Accommodation'}
                            </h4>
                            {activity.departure ? (
                              <p className="text-xs text-red-700">
                                <span className="font-medium">Final departure from:</span> {activity.accommodation_info.hotel_names?.join(', ') || 'property'}
                                {activity.accommodation_info.checkout_date && (
                                  <span className="block mt-1">Checkout: {new Date(activity.accommodation_info.checkout_date).toLocaleDateString('en-GB')}</span>
                                )}
                              </p>
                            ) : activity.checkout ? (
                              <div className="text-xs text-orange-700">
                                {activity.accommodation_info.checkout_date && (
                                  <p className="text-xs">Date: {new Date(activity.accommodation_info.checkout_date).toLocaleDateString('en-GB')}</p>
                                )}
                              </div>
                            ) : (
                              <div>
                                {activity.accommodation_info.hotel_names && activity.accommodation_info.hotel_names.length > 0 && (
                                  <p className="text-xs text-blue-700 mb-1">
                                    <span className="font-medium">Hotel:</span> {activity.accommodation_info.hotel_names.join(', ')}
                                  </p>
                                )}
                                {activity.accommodation_info.checkin_date && (
                                  <p className="text-xs text-blue-700 mb-1">
                                    <span className="font-medium">Check-in:</span> {new Date(activity.accommodation_info.checkin_date).toLocaleDateString('en-GB')}
                                  </p>
                                )}
                                {activity.accommodation_info.checkout_date && (
                                  <p className="text-xs text-blue-700">
                                    <span className="font-medium">Check-out:</span> {new Date(activity.accommodation_info.checkout_date).toLocaleDateString('en-GB')}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Tour Details */}
                        {activity.day_detail?.tour_description && (
                          <div className="mb-3">
                            <h4 className="text-xs font-semibold text-gray-800 mb-2">📍 Tour Details</h4>
                            <div className="text-xs text-gray-700 bg-gray-50 p-3 rounded-lg leading-relaxed whitespace-pre-line">
                              {activity.day_detail.tour_description.replace(/^-\s*/gm, '• ')}
                            </div>
                          </div>
                        )}
                        
                        {/* Meals */}
                        {activity.day_detail?.meals && activity.day_detail.meals.length > 0 && (
                          <div className="mb-3">
                            <h4 className="text-xs font-semibold text-gray-800 mb-2">🍽️ Meals</h4>
                            <div className="bg-yellow-50 p-3 rounded-lg">
                              {activity.day_detail.meals.map((meal: any, idx: number) => (
                                <p key={idx} className="text-xs text-yellow-800 mb-1">
                                  <span className="font-medium">{meal.meal_name}</span>
                                  {meal.meal_time && <span> at {meal.meal_time}</span>}
                                  {meal.meal_location && <span> - {meal.meal_location}</span>}
                                </p>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Addons */}
                        {((activity.day_detail?.day_addons && activity.day_detail.day_addons.length > 0) || 
                          (activity.day_detail?.day_extras && activity.day_detail.day_extras.length > 0)) && (
                          <div>
                            <h4 className="text-xs font-semibold text-gray-800 mb-2">✨ Addons Included:</h4>
                            <div className="bg-green-50 p-3 rounded-lg">
                              {activity.day_detail.day_addons?.map((addon: any, idx: number) => (
                                <p key={`addon-${idx}`} className="text-xs text-green-700 mb-1">
                                  {addon.activity_name && (
                                    <span className="font-medium">{addon.quantity && addon.quantity > 0 ? addon.quantity : 1}x {addon.activity_name}</span>
                                  )}
                                  {addon.main_activity_name && addon.main_activity_name !== 'General' && (
                                    <span className="text-green-500 ml-1">({addon.main_activity_name})</span>
                                  )}
                                  {addon.activity_description && (
                                    <span className="block text-green-600 ml-2">{addon.activity_description}</span>
                                  )}
                                </p>
                              ))}
                              {activity.day_detail.day_extras?.filter((extra: any) => 
                                extra.display_in_itinerary !== false && extra.number > 0 && extra.extra_text
                              ).map((extra: any, idx: number) => (
                                <p key={`extra-${idx}`} className="text-xs text-green-700 mb-1">
                                  <span className="font-medium">{extra.number}x {extra.extra_text}</span>
                                  {extra.main_activity_name && extra.main_activity_name !== 'General' && (
                                    <span className="text-green-500 ml-1">({extra.main_activity_name})</span>
                                  )}
                                </p>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              {/* Desktop List View */}
              <div className="hidden sm:block space-y-3">
                {activities.map((activity, index) => (
                  <div
                    key={index}
                    className="bg-gray-50 border border-gray-200 rounded-xl overflow-hidden hover:shadow-md transition-all"
                  >
                    <button
                      onClick={() => toggleActivity(index)}
                      className="w-full px-4 py-4 flex items-center justify-between hover:bg-gray-50 transition-all"
                    >
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-sm font-mono text-blue-600">WH_{activity.itinerary_id}</span>
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">Day {activity.day}</span>
                        </div>
                        <div className="text-left min-w-0 flex-1">
                          <div className="font-medium text-base text-gray-900 truncate">{activity.lead_guest}</div>
                          <div className="text-sm text-gray-600 mt-1">
                            <div className="truncate">{activity.tour_title}</div>
                            {activity.agency_name && (
                              <div className="text-xs text-gray-500 mt-1">• {activity.agency_name}</div>
                            )}
                          </div>
                          {activity.accommodation_info?.hotel_names && activity.accommodation_info.hotel_names.length > 0 && (
                            <div className="text-xs text-gray-500 mt-1 truncate">
                              🏨 {activity.accommodation_info.hotel_names.join(', ')}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {activity.checkout && (
                          <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">
                            {activity.accommodation_info?.checkout_date ? 
                              `Checkout ${new Date(activity.accommodation_info.checkout_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}` : 
                              'Checkout'
                            }
                          </span>
                        )}
                        {activity.departure && (
                          <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">
                            Departure
                          </span>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            copyActivityText(activity);
                          }}
                          className="p-2 hover:bg-blue-100 text-blue-600 rounded-lg transition-colors"
                          title="Copy WhatsApp message"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                        {expandedActivities.has(index) ? 
                          <ChevronUp className="h-5 w-5 text-gray-400" /> : 
                          <ChevronDown className="h-5 w-5 text-gray-400" />
                        }
                      </div>
                    </button>

                    {expandedActivities.has(index) && (
                      <div className="px-4 pb-4 bg-gray-50">
                        <div className="bg-white p-4 rounded-lg">
                          {/* Accommodation Information */}
                          {activity.accommodation_info && (activity.accommodation_info.hotel_names?.length > 0 || activity.accommodation_info.checkin_date || activity.accommodation_info.checkout_date) && (
                            <div className={`p-3 rounded-lg mb-4 ${
                              activity.departure ? 'bg-red-50' : 
                              activity.checkout ? 'bg-orange-50' : 'bg-blue-50'
                            }`}>
                              <h4 className={`text-sm font-medium mb-2 ${
                                activity.departure ? 'text-red-800' : 
                                activity.checkout ? 'text-orange-800' : 'text-blue-800'
                              }`}>
                                {activity.departure ? '✈️ Departure Day Details' : 
                                 activity.checkout ? '🏨 Checkout Day Details' : '🏨 Accommodation Details'}
                              </h4>
                              {activity.departure ? (
                                <div className="text-sm text-red-700">
                                  <div className="mb-2 p-2 bg-red-100 rounded border-l-4 border-red-400">
                                    <div className="font-medium">🎯 Final Day - Departure</div>
                                    <div className="text-xs mt-1">This is the last day of the journey</div>
                                  </div>
                                  {activity.accommodation_info.hotel_names && activity.accommodation_info.hotel_names.length > 0 && (
                                    <div className="mb-1">
                                      <span className="font-medium">Departing from:</span> {activity.accommodation_info.hotel_names.join(', ')}
                                    </div>
                                  )}
                                  {activity.accommodation_info.checkout_date && (
                                    <div className="text-sm">
                                      <span className="font-medium">Checkout:</span> {new Date(activity.accommodation_info.checkout_date).toLocaleDateString('en-GB', { 
                                        day: '2-digit', 
                                        month: 'short', 
                                        year: 'numeric' 
                                      })}
                                    </div>
                                  )}
                                </div>
                              ) : activity.checkout ? (
                                <div className="text-sm text-orange-700">
                                  <div className="mb-2 p-2 bg-orange-100 rounded border-l-4 border-orange-400">
                                    <div className="font-medium">📦 Checkout Day</div>
                                    <div className="text-xs mt-1">Moving to next accommodation</div>
                                  </div>

                                  {activity.accommodation_info.checkout_date && (
                                    <div className="text-sm">
                                      <span className="font-medium">Checkout date:</span> {new Date(activity.accommodation_info.checkout_date).toLocaleDateString('en-GB', { 
                                        day: '2-digit', 
                                        month: 'short', 
                                        year: 'numeric' 
                                      })}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div>
                                  {activity.accommodation_info.hotel_names && activity.accommodation_info.hotel_names.length > 0 && (
                                    <div className="text-sm text-blue-700 mb-1">
                                      <span className="font-medium">Hotels:</span> {activity.accommodation_info.hotel_names.join(', ')}
                                    </div>
                                  )}
                                  {activity.accommodation_info.checkin_date && (
                                    <div className="text-sm text-blue-700 mb-1">
                                      <span className="font-medium">Check-in:</span> {new Date(activity.accommodation_info.checkin_date).toLocaleDateString('en-GB', { 
                                        day: '2-digit', 
                                        month: 'short', 
                                        year: 'numeric' 
                                      })}
                                    </div>
                                  )}
                                  {activity.accommodation_info.checkout_date && (
                                    <div className="text-sm text-blue-700">
                                      <span className="font-medium">Check-out:</span> {new Date(activity.accommodation_info.checkout_date).toLocaleDateString('en-GB', { 
                                        day: '2-digit', 
                                        month: 'short', 
                                        year: 'numeric' 
                                      })}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                          
                          {/* Tour Description */}
                          {activity.day_detail?.tour_description && (
                            <div className="mb-4">
                              <h4 className="text-sm font-medium text-gray-800 mb-2">📍 Tour Details</h4>
                              <div className="text-sm text-gray-700 whitespace-pre-line leading-relaxed bg-gray-50 p-3 rounded-lg">
                                {activity.day_detail.tour_description.replace(/^-\s*/gm, '• ')}
                              </div>
                            </div>
                          )}
                          
                          {activity.day_detail?.meals && activity.day_detail.meals.length > 0 && (
                            <div className="mb-3">
                              <h4 className="text-sm font-medium text-gray-800 mb-2">🍽️ Meals Included:</h4>
                              <div className="bg-yellow-50 p-3 rounded-lg">
                                {activity.day_detail.meals.map((meal: any, idx: number) => (
                                  <div key={idx} className="text-sm text-yellow-800 mb-1">
                                    <span className="font-medium">{meal.meal_name}</span>
                                    {meal.meal_time && <span className="text-yellow-700"> at {meal.meal_time}</span>}
                                    {meal.meal_location && <span className="text-yellow-700"> - {meal.meal_location}</span>}
                                    {meal.quantity && meal.quantity > 1 && <span className="text-yellow-600"> (${meal.quantity} pax)</span>}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {((activity.day_detail?.day_addons && activity.day_detail.day_addons.length > 0) || 
                            (activity.day_detail?.day_extras && activity.day_detail.day_extras.length > 0)) && (
                            <div className="mb-3">
                              <h4 className="text-sm font-medium text-gray-800 mb-2">✨ Addons Included:</h4>
                              <div className="bg-green-50 p-3 rounded-lg">
                                {activity.day_detail.day_addons?.map((addon: any, idx: number) => (
                                  <div key={`addon-${idx}`} className="text-sm text-green-700 mb-1">
                                    {addon.activity_name && (
                                      <span className="font-medium">
                                        {addon.quantity && addon.quantity > 0 ? addon.quantity : 1}x {addon.activity_name}
                                      </span>
                                    )}
                                    {addon.main_activity_name && addon.main_activity_name !== 'General' && (
                                      <span className="text-green-500 ml-1">({addon.main_activity_name})</span>
                                    )}
                                    {addon.activity_description && (
                                      <div className="text-green-600 ml-2">{addon.activity_description}</div>
                                    )}
                                  </div>
                                ))}
                                {activity.day_detail.day_extras?.filter((extra: any) => 
                                  extra.display_in_itinerary !== false && extra.number > 0 && extra.extra_text
                                ).map((extra: any, idx: number) => (
                                  <div key={`extra-${idx}`} className="text-sm text-green-700 mb-1">
                                    <span className="font-medium">{extra.number}x {extra.extra_text}</span>
                                    {extra.main_activity_name && extra.main_activity_name !== 'General' && (
                                      <span className="text-green-500 ml-1">({extra.main_activity_name})</span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CalendarView;